import {TFunction} from 'i18next';
import {BADGE_MAP} from '../../common/constant';
import {
  getContentProps,
  getImageUrl,
  getTextWithEllipsis,
  I_Badge,
} from '../../common/state/apiGet';
import {ITicketType} from '../../common/type';
import {
  formatFromServerDate,
  getDateDifference,
  getDuration,
  getNowFromServer,
  getIsBetween,
  getRemainText,
  getRemainTextShort,
  getTimeAgoText,
  getTimeStamp,
  SECONDS_OF_A_DAY,
  SHORT_DATE_FORMAT,
} from '../../common/util/date';
import {toCamelCase} from '../../common/util/string';
import {CONTENT_TITLE_TOKEN} from '../constant';
import {
  IEpisodeItem,
  IEpisodeType,
  IMessageType,
  IOriginalProduct,
  IResultUseTicket,
  IVoucher,
  IWaitfree,
} from '../type';

export function getWaitfree(data: I_Waitfree): IWaitfree {
  const {totalSeconds} = getDuration(data.interval);
  const chargeAtTs = data.rechargesDateTime
    ? getTimeStamp(data.rechargesDateTime)
    : undefined;
  const nowFromServer = getNowFromServer(data.responseDateTime);
  const chargeRatio =
    !chargeAtTs || chargeAtTs <= nowFromServer
      ? 1
      : Math.min(1, (chargeAtTs - nowFromServer) / totalSeconds / 1000);
  return {
    excludedEpisodeCount: data.excludeEpisodeCount || 0,
    intervalMinutes: Math.floor(totalSeconds / 60),
    chargeAtTs,
    nowFromServer,
    chargeRatio,
    isCharging: chargeRatio < 1,
    isCharged: data.charged,
  };
}
export interface I_Waitfree {
  excludeEpisodeCount: number;
  interval: string;
  rechargesDateTime?: string;
  responseDateTime: string;
  charged: boolean;
}

export function getEpisodeItem({
  item,
  t,
  now = Date.now(),
}: {
  item: I_EpisodeItem;
  t: TFunction;
  now?: number;
}): IEpisodeItem {
  const type = toCamelCase(item.useType) as IEpisodeType;
  return {
    id: item.id,
    sid: item.seoId,
    contentId: item.contentId,
    order: item.no,
    title: item.title,
    thumbnail: getImageUrl(item.asset.thumbnailImage),
    type,
    readable: item.readable,
    isRead: item.read,
    timeText: getEpisodeTime({item, t, now}),
    ageLimit: item.ageLimit || 0,
    isStopSalesEpisode: item.status !== 'SELLING',
    readDateTimeTs: item.readDateTime
      ? getTimeStamp(item.readDateTime)
      : undefined,
    badge:
      type !== 'pay' && type !== 'earlyAccess' ? BADGE_MAP[type] : undefined,
    readTimeText: item.readDateTime
      ? getTimeAgoText({oldDate: item.readDateTime, now: Date.now()}, t)
      : undefined,
    subText: getEpisodeSubText(item, t),
  };
}
export interface I_EpisodeItem {
  id: number;
  seoId: string;
  contentId: number;
  no: number;
  title: string;
  serialStartDateTime: string;
  asset: {
    thumbnailImage: string;
  };
  useType: string;
  useStartDateTime?: string;
  useEndDateTime?: string;
  readable: boolean;
  read: boolean;
  status: string;
  free?: boolean;
  ageLimit?: number;
  readDateTime?: string;
}

function getEpisodeTime({
  item,
  t,
  now,
}: {
  item: I_EpisodeItem;
  t: TFunction;
  now: number;
}) {
  const {useType, useStartDateTime, useEndDateTime, serialStartDateTime} = item;
  const readFreeStartTs = useEndDateTime && getTimeStamp(useEndDateTime);
  const useEndTs = useEndDateTime ? getTimeStamp(useEndDateTime) : undefined;
  const type = toCamelCase(useType) as IEpisodeType;
  const publishDt = formatFromServerDate(
    serialStartDateTime ?? useStartDateTime,
    SHORT_DATE_FORMAT
  );
  if (type === 'rental' && useEndTs) {
    if (useEndTs > now) {
      return getRemainTextShort({endDate: useEndTs, now}, t);
    } else {
      return publishDt;
    }
  } else if (
    type === 'earlyAccess' &&
    readFreeStartTs &&
    readFreeStartTs > now
  ) {
    const {totalSeconds} = getDateDifference(readFreeStartTs, now);
    const totalDays = Math.ceil(totalSeconds / SECONDS_OF_A_DAY);
    return t('contenthome_preview_day', {count: totalDays});
  } else {
    return publishDt;
  }
}

export function getResultUseTicket({
  data,
  t,
}: {
  data: I_ResultUseTicket;
  t: TFunction;
}): IResultUseTicket {
  return {
    message: data.message,
    messageType: getMessageTypeForPassApi({
      alreadyRented: data.alreadyRented,
      canNotUseWaitForFree: !!data.canNotUseWaitForFree,
      messageType: data.messageType,
    }),
    myTicket: data.my
      ? {
          buyTicketCount: data.my.possessionTicketCount || 0,
          rentTicketCount:
            (data.my.rentalTicketCount || 0) +
            (data.my.giftTicketCount || 0) +
            (data.my.waitForFreeTicketCount || 0),
        }
      : {buyTicketCount: 0, rentTicketCount: 0},
    isPass: data.pass,
    waitfree: data.waitForFree && getWaitfree(data.waitForFree),
    nextEpisode:
      data.episodeLink?.next &&
      getEpisodeItem({item: data.episodeLink.next, t}),
    targetEpisode: data.episode && getEpisodeItem({item: data.episode, t}),
    ticketType: data.ticketType
      ? (toCamelCase(data.ticketType) as ITicketType)
      : undefined,
    cashAmount: data.cashAmount,
    ticketPrice: data.products?.rentalTicket?.price,
    canNotUseWaitForFree: !!data.canNotUseWaitForFree,
  };
}
function getMessageTypeForPassApi({
  alreadyRented,
  canNotUseWaitForFree,
  messageType,
}: {
  alreadyRented: boolean;
  canNotUseWaitForFree: boolean;
  messageType: string;
}): IMessageType {
  if (alreadyRented) {
    return canNotUseWaitForFree
      ? 'alreadyRentedExcludedEpisode'
      : 'alreadyRented';
  } else {
    return canNotUseWaitForFree
      ? 'noWaitForFreeEpisode'
      : (toCamelCase(messageType) as IMessageType);
  }
}

interface I_ResultUseTicket {
  message: string;
  messageType: string;
  my?: {
    possessionTicketCount: number;
    rentalTicketCount: number;
    giftTicketCount: number;
    waitForFreeTicketCount: number;
  };
  pass: boolean;
  waitForFree?: I_Waitfree;
  episodeLink?: {
    next?: I_EpisodeItem;
  };
  episode: I_EpisodeItem;
  ticketType: string;
  alreadyRented: boolean; // TODO: 추후 사용 예정. 현재는 messageType에서 내려주는 값 사용
  // 아래 두 필드는 messageType 이 NO_TICKET 경우만 내려온다
  cashAmount?: number;
  products?: {
    rentalTicket: {price: number};
  };
  canNotUseWaitForFree?: boolean; // 기다무로 열람 불가능한 회차인 경우 true
}

export function getOriginalProduct(
  data: I_OriginalProduct
): IOriginalProduct | undefined {
  if (!data?.originalNovel?.badges) {
    return undefined;
  }
  return {
    title: getTextWithEllipsis(data.title, {
      [CONTENT_TITLE_TOKEN]: data.contentTitle,
    }),
    originalNovel: {
      content: getContentProps(
        {
          id: data.originalNovel.id,
          title: data.contentTitle,
          titleImageB: data.originalNovel.titleImage,
          backgroundImage: data.originalNovel.thumbnailImage,
          badges: data.originalNovel.badges,
          backgroundColor: data.originalNovel.backgroundColor,
        },
        ['id', 'title', 'titleImg', 'bgImg', 'badges', 'bgColorRgb'],
        {isAlphaBg: true}
      ),
    },
  };
}
export interface I_OriginalProduct {
  title: string;
  contentTitle: string;
  originalNovel: {
    id: string;
    titleImage: string;
    thumbnailImage: string;
    badges: I_Badge[];
    backgroundColor: string;
  };
}

export function getVoucher(voucher: I_Voucher, t: TFunction): IVoucher {
  let remainText = '';
  let isDiscount = false;

  if (voucher?.discount) {
    isDiscount = true;

    const {toDateTime, fromDateTime} = voucher.discount;

    if (getIsBetween(fromDateTime, toDateTime)) {
      remainText = getRemainText(
        {endDate: toDateTime, now: Date.now(), isShortUnit: true},
        t
      );
    }
  }

  return {
    discountedPrice: voucher.discount?.discountedPrice || 0,
    remainText,
    isDiscount,
    list: voucher.packages
      ?.map((ticket: I_Package) => ({
        bundleCount: ticket.allTicketCount,
        bonusCount: ticket.bonusTicketCount,
        count: ticket.ticketCount,
        name: ticket.name,
        price: voucher.price,
        isBuyAtOnce: ticket.name === 'buy-at-once',
        serverData: ticket.included,
        ticketId: ticket.included?.[0]?.ticketPackageId,
      }))
      .filter(ticket => ticket.count > 0),
  };
}

export function getEpisodeSubText(episode: I_EpisodeItem, t?: TFunction) {
  if (!t) return;

  const {useEndDateTime, useType} = episode;

  const now = Date.now();
  const readFreeStartTs = useEndDateTime && getTimeStamp(useEndDateTime);
  const useEndTs = useEndDateTime ? getTimeStamp(useEndDateTime) : undefined;
  const type = toCamelCase(useType) as IEpisodeType;

  if (type === 'rental' && useEndTs && useEndTs > now) {
    return getRemainTextShort({endDate: useEndTs, now}, t);
  } else if (
    type === 'earlyAccess' &&
    readFreeStartTs &&
    readFreeStartTs > now
  ) {
    const {totalSeconds} = getDateDifference(readFreeStartTs, now);
    const totalDays = Math.ceil(totalSeconds / SECONDS_OF_A_DAY);
    return t('contenthome_preview_day', {count: totalDays});
  } else if (type === 'waitForFree') {
    return t('common_free_available');
  }
}

interface I_Discount {
  discountedPrice: number;
  fromDateTime: string;
  toDateTime: string;
}

interface I_Included {
  quantity: number;
  ticketPackageId: number;
}

interface I_Package {
  allTicketCount: number;
  bonusTicketCount: number;
  id: number;
  included: I_Included[];
  name: string;
  ticketCount: number;
}

export interface I_Voucher {
  discount?: I_Discount;
  packages: I_Package[];
  price: number;
}
